import { TEST_IDS } from '@va/constants';
import { useLocale } from '@va/localization';
import { addNumberSeparator } from '@va/util/helpers';
import { Chart, ChartConfiguration } from 'chart.js';
import classNames from 'classnames';
import { assign } from 'lodash';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import '../registerChartPlugins';

type DoughnutChartDatasetType = {
  label?: string;
  data: number[];
  backgroundColor: string[];
  hoverOffset?: number;
};

type DoughnutChartDataType = {
  canvasId: string;
  wrapperClassName?: string;
  datasets: DoughnutChartDatasetType[];
  labels?: ReactNode[];
  content?: React.ReactNode;
  cutout?: number;
  tooltip?: (ctx: any) => string;
  size?: number;
  disableTooltip?: boolean;
  onSegmentHover?: (index: number | null) => void;
};

export const DoughnutChartV2: React.FC<DoughnutChartDataType> = ({
  canvasId,
  wrapperClassName,
  datasets,
  labels = [],
  content,
  cutout = 90,
  tooltip,
  size,
  disableTooltip,
  onSegmentHover,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart<'doughnut'>>(null);

  const { locale } = useLocale();

  const defaultTooltip = useCallback(
    (context: any) => {
      const label = labels[context.dataIndex];
      if (!label) return addNumberSeparator(context.raw, locale);
      return `${labels[context.dataIndex]}  ${addNumberSeparator(context.raw, locale)}`;
    },
    [labels, locale],
  );

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvasContext = canvasRef.current.getContext('2d');

    if (!canvasContext) return;

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [...datasets],
      },
      options: {
        plugins: {
          datalabels: {
            display: false,
          },
          tooltip: {
            enabled: !disableTooltip,
            callbacks: {
              label: tooltip || defaultTooltip,
            },
          },
        },
        cutout: cutout,
        ...(onSegmentHover && {
          onHover: (_event: any, elements: any[]) => {
            onSegmentHover(elements.length > 0 ? elements[0].index : null);
          },
        }),
      },
    };

    if (!chartRef.current) {
      chartRef.current = new Chart<'doughnut'>(canvasContext, config);
      return;
    }

    // If chart exists, update it
    updateChart(chartRef.current, config);
  }, [cutout, datasets, defaultTooltip, disableTooltip, labels, locale, onSegmentHover, tooltip]);

  return (
    <div className={classNames('relative', wrapperClassName)}>
      <div className='absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none overflow-hidden'>
        {content}
      </div>
      <canvas
        id={canvasId}
        data-testid={TEST_IDS.helpers.createChartCanvasId(canvasId)}
        ref={canvasRef}
        height={size}
        width={size}
      ></canvas>
    </div>
  );
};

function updateChart(chart: Chart<'doughnut'>, config: ChartConfiguration<'doughnut'>) {
  // Update chart options
  if (config.options) {
    chart.options = config.options;
  }

  // Update chart datasets
  if (chart.data.datasets.length !== config.data.datasets.length) {
    // If the nr of datasets is different, reassign the object
    chart.data.datasets = config.data.datasets;
  } else {
    for (let i = 0; i < config.data.datasets.length; i++) {
      const dataset = config.data.datasets[i];
      assign(chart.data.datasets[i], dataset);
    }
  }

  // Update chart labels
  if (chart.data.labels) {
    chart.data.labels = config.data.labels;
  }

  // Re-draw chart
  chart.update();
}
